// Conexão da plateia: o canal separado da mesa, que só traz o que está na mesa (cartas jogadas, vira, placar, pedidos
// e resultados). O estado sai no formato da mesa dos jogadores, visto do lugar de baixo, para a Mesa desenhar a mesma
// mesa sem mão e sem botões. Reconecta sozinha, como a conexão de quem joga.
import { team } from '@truco6/regras';
import type { PlayerView, Seat, VisibleEvent } from '@truco6/regras';
import type {
  CodigoErro, LugarPublico, MensagemCliente, MensagemServidor, Perfil, SalaDaPlateia, SalaPublica, VisaoDaPlateia,
} from '@truco6/mesa/protocolo';
import type { EstadoMesa, OuvinteEventos, OuvinteRecado, SocketLike } from './conexao.ts';

const ABERTO = 1;
// Recusas que encerram a plateia: a tela mostra o motivo (e o atalho para Planos, ou a tela de guardar a conta).
const FATAIS: CodigoErro[] = ['sala_inexistente', 'sala_encerrada', 'token_invalido', 'outra_conexao', 'sem_permissao', 'sem_plano', 'plateia_cheia', 'conta_obrigatoria', 'tirado_da_plateia', 'so_membros'];

export interface EstadoPlateia extends EstadoMesa {
  plateia: SalaDaPlateia | null;
}

// A sala da plateia no formato da mesa: rostos e apelidos, sem nada de quem joga além disso.
export function salaNaMesa(s: SalaDaPlateia): SalaPublica {
  return {
    codigo: s.codigo ?? '', nome: s.nome, publica: s.publica, dono: '', fase: s.fase, partida: s.partida,
    regras: { sinais: 'aviso', tempoPorJogadaMs: s.tempoPorJogadaMs, vagasComBot: true, zoeira: false }, esperando: [], tentos: null, liga: null, visual: s.visual,
    lugares: s.lugares.map((l): LugarPublico | null => {
      if (!l) return null;
      const perfil = { apelido: l.apelido, avatar: l.avatar };
      if (l.tipo === 'bot') return { tipo: 'bot', perfil, ...(l.vagaAberta ? { vagaAberta: true as const } : {}) };
      return {
        tipo: 'pessoa', id: l.id ?? '', perfil, conectado: l.conectado, bot: !!l.bot, ...(l.bot ? { botApelido: l.apelido } : {}),
        ...(l.nivel ? { nivel: l.nivel } : {}), ...(l.moldura ? { moldura: l.moldura } : {}), ...(l.coroa ? { coroa: true as const } : {}),
        ...(l.vagaAberta ? { vagaAberta: true as const } : {}), ...(l.entraNaMao ? { entraNaMao: 0 } : {}),
      };
    }),
  };
}

// A mesa da plateia vista do lugar de baixo: sem cartas na mão e sem cartas de parceiro.
export function visaoNaMesa(v: VisaoDaPlateia, eu: Seat): PlayerView {
  return {
    seat: eu, team: team(eu), phase: v.phase, handNo: v.handNo, scores: v.scores, dealer: v.dealer, mao: v.mao, winner: v.winner,
    hand: v.hand ? { ...v.hand, canRaiseTeam: null, myCards: [], partnerCards: {} } : null,
  };
}

export class ConexaoPlateia {
  private ws: SocketLike | null = null;
  private estado: EstadoPlateia = {
    status: 'conectando', erro: null, aviso: null, usuario: null, anonimo: true, sala: null, eu: 0, partida: 0, seq: 0, visao: null,
    votacao: null, prazos: [], desvio: 0, ganhos: null, rodagemAoVivo: null, zoeira: false, faltaCelular: false, limite: null, semPartidas: false, plateia: null,
  };
  private readonly ouvintes = new Set<() => void>();
  private readonly ouvintesEventos = new Set<OuvinteEventos>();
  private readonly ouvintesRecado = new Set<OuvinteRecado>();
  private tentativas = 0;
  private encerrada = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly url: string;
  private readonly token: () => Promise<string>;
  private readonly perfil: Perfil;
  private readonly alvo: { codigo: string } | { amigo: string };
  private readonly criarSocket: (url: string) => SocketLike;

  constructor(
    url: string, token: () => Promise<string>, perfil: Perfil, alvo: { codigo: string } | { amigo: string },
    criarSocket: (url: string) => SocketLike = (u) => new WebSocket(u) as unknown as SocketLike,
  ) {
    this.url = url;
    this.token = token;
    this.perfil = perfil;
    this.alvo = alvo;
    this.criarSocket = criarSocket;
  }

  obter = (): EstadoPlateia => this.estado;
  assinar = (fn: () => void): (() => void) => { this.ouvintes.add(fn); return () => this.ouvintes.delete(fn); };
  aoEventos(fn: OuvinteEventos): () => void { this.ouvintesEventos.add(fn); return () => this.ouvintesEventos.delete(fn); }
  aoRecado(fn: OuvinteRecado): () => void { this.ouvintesRecado.add(fn); return () => this.ouvintesRecado.delete(fn); }
  agoraServidor(): number { return Date.now() + this.estado.desvio; }

  iniciar(): this { this.conectar(); return this; }

  encerrar(): void {
    this.encerrada = true;
    if (this.timer) clearTimeout(this.timer);
    this.ws?.close(1000, 'saiu');
    this.mudar({ status: 'encerrada' });
  }

  enviar(m: MensagemCliente): boolean {
    if (this.ws?.readyState !== ABERTO) return false;
    this.ws.send(JSON.stringify(m));
    return true;
  }

  reagir(emoji: string): boolean { return this.enviar({ t: 'reacao_plateia', emoji }); }

  // Quem é o lugar de baixo: o amigo que você veio assistir, se ele está sentado; senão, o primeiro lugar.
  private lugarDeBaixo(s: SalaDaPlateia): Seat {
    const amigo = 'amigo' in this.alvo ? this.alvo.amigo : null;
    const i = amigo ? s.lugares.findIndex((l) => l?.id === amigo) : -1;
    return (i < 0 ? 0 : i) as Seat;
  }

  private conectar(): void {
    if (this.encerrada) return;
    const ws = this.ws = this.criarSocket(this.url);
    ws.onopen = () => {
      void this.token().then(
        (token) => { if (this.ws === ws) ws.send(JSON.stringify({ t: 'ola', token })); },
        () => this.mudar({ erro: { codigo: 'token_invalido', mensagem: 'Sua sessão venceu. Entre de novo.' } }),
      );
    };
    ws.onmessage = (ev) => { try { this.receber(JSON.parse(String(ev.data)) as MensagemServidor); } catch { /* mensagem quebrada */ } };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.encerrada || ev.code === 4001 || (this.estado.erro && FATAIS.includes(this.estado.erro.codigo as CodigoErro))) {
        this.mudar({ status: 'encerrada' });
        return;
      }
      const espera = ev.code === 4000 ? 300 : Math.min(5000, 400 * 2 ** this.tentativas);
      this.tentativas++;
      this.mudar({ status: 'reconectando' });
      this.timer = setTimeout(() => this.conectar(), espera);
    };
    ws.onerror = () => {};
  }

  private receber(m: MensagemServidor): void {
    const agora = Date.now();
    switch (m.t) {
      case 'bemvindo':
        this.tentativas = 0;
        this.mudar({ status: 'aberta', erro: null, usuario: m.usuario, anonimo: m.anonimo, desvio: m.agora - agora });
        this.enviar({ t: 'assistir', ...this.alvo, perfil: this.perfil });
        return;
      case 'plateia_estado': {
        const eu = this.lugarDeBaixo(m.sala);
        this.mudar({
          plateia: m.sala, sala: salaNaMesa(m.sala), eu, partida: m.partida, seq: m.seq, visao: m.mesa ? visaoNaMesa(m.mesa, eu) : null,
          prazos: m.prazos, desvio: m.agora - agora, erro: null,
        });
        return;
      }
      case 'plateia_atualizacao': {
        // Pulou uma mensagem ou mudou de partida sem o retrato novo: pede tudo de novo.
        if (m.partida !== this.estado.partida || m.desde !== this.estado.seq) { this.enviar({ t: 'sincronizar' }); return; }
        this.estado = { ...this.estado, seq: m.seq, visao: visaoNaMesa(m.mesa, this.estado.eu ?? 0), prazos: m.prazos, desvio: m.agora - agora };
        // Os eventos da plateia têm o mesmo formato dos eventos da mesa, sem sinal.
        const eventos = m.eventos.map((x) => ({ seq: x.seq, ev: x.ev as VisibleEvent }));
        if (eventos.length) for (const f of this.ouvintesEventos) f(eventos, this.estado);
        this.mudar({});
        return;
      }
      case 'plateia_sala': {
        // Sala de espera (antes da partida ou depois da revanche): sem mesa.
        const eu = this.estado.plateia ? this.estado.eu ?? 0 : this.lugarDeBaixo(m.sala);
        this.mudar({ plateia: m.sala, sala: salaNaMesa(m.sala), desvio: m.agora - agora, eu, ...(m.sala.fase === 'espera' ? { visao: null } : {}) });
        return;
      }
      case 'reacao_plateia':
        for (const f of this.ouvintesRecado) f(m);
        return;
      case 'erro':
        if (FATAIS.includes(m.codigo)) this.mudar({ erro: { codigo: m.codigo, mensagem: m.mensagem } });
        else this.mudar({ aviso: { codigo: m.codigo, mensagem: m.mensagem, em: agora } });
        return;
      default:
        return;
    }
  }

  private mudar(parte: Partial<EstadoPlateia>): void {
    this.estado = { ...this.estado, ...parte };
    for (const f of this.ouvintes) f();
  }
}
